/*******************************
 * This component uses jotai async derived atoms, 
 * so it requires suspense wrapper around it when used
/*******************************/

import { useAtomValue } from 'jotai';
import React, { useState } from 'react';
import { Collapse } from 'reactstrap';

import {
  historyChartDataAtom,
  historyChartLabelsAtom,
  intervalAtom,
  newDataGenerationIndexAtom,
  appliedRowIdsAtom,
  severityFiltersAtom,
  showHistoryChartCountsAtom,
  sastTypeAtom,
  LastSyncDateAtom,
  sastCurrentVulnCountAtom,
} from './state';
import HistoryChart from '../components/HistoryChart';
import {
  getSeverityFilterValues,
  getVulnStatusSum,
  getLastIntervalDate,
  getHistoryDrillDownUrl,
  getChartSubtitle,
  getLastIntervalCount,
} from '../helper';
import { IntervalFilter, TooltipLabelsType, VulnStatus } from '../types';
import '../styles.css';

type Props = {
  tooltipLabels: TooltipLabelsType;
  setModalComponentData: (ModalDialogData) => void;
};

const HistoryCharts = ({ tooltipLabels }: Props) => {
  const showHistoryChartCounts = useAtomValue(showHistoryChartCountsAtom);
  const LastSyncDate = useAtomValue(LastSyncDateAtom);
  const historyChartData = useAtomValue(historyChartDataAtom);
  const historyChartLabels = useAtomValue(historyChartLabelsAtom);
  const newDataGenerationIndex = useAtomValue(newDataGenerationIndexAtom);
  const interval = useAtomValue(intervalAtom);
  const severityFilters = useAtomValue(severityFiltersAtom);
  const appliedSASTIds = useAtomValue(appliedRowIdsAtom);
  const sastType = useAtomValue(sastTypeAtom);
  const sastCurrentVulnCount = useAtomValue(sastCurrentVulnCountAtom);

  const [expandedChartKey, setExpandedChartKey] = useState<string | undefined>(
    undefined,
  );
  const [expandedChartOpen, setExpandedChartOpen] = useState(false);

  function getHistoryChartsDrillDownUrl(type: string, issueType: string) {
    if (!type) type = 'all';
    if (issueType === 'Bug') issueType = 'BUG';
    if (issueType === 'Vulnerability') issueType = 'VULNERABILITY';

    let commonParameters = {
      type: type,
      severity: '',
      sourceType: 'sast2',
      issueType: issueType,
      qid: '',
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: appliedSASTIds,
    };

    let lastIntervalDate = getLastIntervalDate(interval);

    const historyChartDrillDownUrl = getHistoryDrillDownUrl(
      commonParameters,
      type,
      lastIntervalDate,
    );

    return historyChartDrillDownUrl;
  }

  // This function returns the date since we are counting the number of vulnerabilities
  function getChartTitleDate(prefix: string) {
    if (historyChartLabels.length === 1) {
      return `${prefix} (All)`;
    }
    let lastCompleteDateLabel = historyChartLabels[0];
    if (historyChartLabels.length > 2) {
      lastCompleteDateLabel = historyChartLabels[historyChartLabels.length - 2];
    }
    let currentYear = new Date().getFullYear();
    let fullDateString = lastCompleteDateLabel + ' ' + currentYear;
    let lastCompleteDate = new Date(fullDateString);
    lastCompleteDate.setDate(lastCompleteDate.getDate() + 1);
    return `${prefix} (Since ${lastCompleteDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })})`;
  }

  function getChartTitle(prefix: string) {
    switch (interval) {
      case IntervalFilter.Week:
      case IntervalFilter.FourWeeks:
      case IntervalFilter.ThreeMonth:
      case IntervalFilter.SixMonth:
        return getChartTitleDate(prefix);
      default:
        return prefix;
    }
  }

  const chartPropertiesDictionary = {
    new: {
      title: getChartTitle('New'),
      subtitle: getChartSubtitle('New', interval),
      lastIntervalCount: getLastIntervalCount(
        VulnStatus.New,
        historyChartData?.historyChartLastIntervalSeverityData,
      ),
      labels: historyChartLabels,
      tooltipLabels: tooltipLabels,
      datasets: historyChartData
        ? historyChartData.historyChartSeverityData[VulnStatus.New]
        : [],
      onExpandClick: () => setExpandedChartComponent('new'),
      // colors: ["rgb(255,105,0)", chroma("rgb(255,105,0)").darken(1.5).css()],
      colors: ['#055193', '#0096FF', '#000000'],
      showCounts: showHistoryChartCounts,
      lastIntervalDrillDownUrl: getHistoryChartsDrillDownUrl(
        'lastIntervalNew',
        sastType,
      ),
      getDrillDownUrl: (issueType) =>
        getHistoryChartsDrillDownUrl('new', issueType),
      newDataGenerationIndex,
      selectedHostType: sastType,
      selectedInterval: interval,
      currentVulnCount: getVulnStatusSum(
        VulnStatus.New,
        historyChartData?.historyChartSeverityData,
      ),
      // overrideTotalCount: getTotalCount(historyChartData ? historyChartData[VulnStatus.New] : [])
    },
    reopened: {
      title: getChartTitle('Reopened'),
      subtitle: getChartSubtitle('Reopened', interval),
      lastIntervalCount: getLastIntervalCount(
        VulnStatus.Reopened,
        historyChartData?.historyChartLastIntervalSeverityData,
      ),
      labels: historyChartLabels,
      tooltipLabels: tooltipLabels,
      datasets: historyChartData
        ? historyChartData.historyChartSeverityData[VulnStatus.Reopened]
        : [],
      onExpandClick: () => setExpandedChartComponent('reopened'),
      // colors: ["rgb(254,45,45)", chroma("rgb(254,45,45)").darken(1.5).css()],
      colors: ['#055193', '#0096FF', '#000000'],
      showCounts: showHistoryChartCounts,
      lastIntervalDrillDownUrl: getHistoryChartsDrillDownUrl(
        'lastIntervalReopened',
        sastType,
      ),
      getDrillDownUrl: (issueType) =>
        getHistoryChartsDrillDownUrl('reopened', issueType),
      newDataGenerationIndex,
      selectedHostType: sastType,
      selectedInterval: interval,
      currentVulnCount: getVulnStatusSum(
        VulnStatus.Reopened,
        historyChartData?.historyChartSeverityData,
      ),
      // overrideTotalCount: getTotalCount(historyChartData ? historyChartData[VulnStatus.Reopened] : [])
    },
    fixed: {
      title: getChartTitle('Fixed'),
      subtitle: getChartSubtitle('Fixed', interval),
      lastIntervalCount: getLastIntervalCount(
        VulnStatus.Fixed,
        historyChartData?.historyChartLastIntervalSeverityData,
      ),
      labels: historyChartLabels,
      tooltipLabels: tooltipLabels,
      datasets: historyChartData
        ? historyChartData.historyChartSeverityData[VulnStatus.Fixed]
        : [],
      onExpandClick: () => setExpandedChartComponent('fixed'),
      // colors: ["rgb(113,206,126)", chroma("rgb(113,206,126)").darken(1.5).css()],
      colors: ['#055193', '#0096FF', '#000000'],
      showCounts: showHistoryChartCounts,
      lastIntervalDrillDownUrl: getHistoryChartsDrillDownUrl(
        'lastIntervalFixed',
        sastType,
      ),
      getDrillDownUrl: (issueType) =>
        getHistoryChartsDrillDownUrl('fixed', issueType),
      newDataGenerationIndex,
      selectedHostType: sastType,
      selectedInterval: interval,
      currentVulnCount: getVulnStatusSum(
        VulnStatus.Fixed,
        historyChartData?.historyChartSeverityData,
      ),
      // overrideTotalCount: getTotalCount(historyChartData ? historyChartData[VulnStatus.Fixed] : [])
    },
    ignored: {
      title: getChartTitle('Ignored'),
      subtitle: getChartSubtitle('Ignored', interval),
      lastIntervalCount: getLastIntervalCount(
        VulnStatus.Ignored,
        historyChartData?.historyChartLastIntervalSeverityData,
      ),
      labels: historyChartLabels,
      tooltipLabels: tooltipLabels,
      datasets: historyChartData
        ? historyChartData.historyChartSeverityData[VulnStatus.Ignored]
        : [],
      onExpandClick: () => setExpandedChartComponent('ignored'),
      // colors: ["rgb(252,185,0)", chroma("rgb(252,185,0)").darken(1.5).css()],
      colors: ['#055193', '#0096FF', '#000000'],
      showCounts: showHistoryChartCounts,
      lastIntervalDrillDownUrl: getHistoryChartsDrillDownUrl(
        'lastIntervalIgnored',
        sastType,
      ),
      getDrillDownUrl: (issueType) =>
        getHistoryChartsDrillDownUrl('ignored', issueType),
      newDataGenerationIndex,
      selectedHostType: sastType,
      selectedInterval: interval,
      currentVulnCount: getVulnStatusSum(
        VulnStatus.Ignored,
        historyChartData?.historyChartSeverityData,
      ),
      // overrideTotalCount: getTotalCount(historyChartData ? historyChartData[VulnStatus.Ignored] : [])
    },
  };

  const setExpandedChartComponent = (propertiesKey: string) => {
    // console.log("setExpandedChartComponent", propertiesKey);
    if (expandedChartKey === propertiesKey) {
      setExpandedChartOpen(false);
      setTimeout(() => {
        setExpandedChartKey(undefined);
      }, 1000);
    } else if (expandedChartKey !== undefined) {
      setExpandedChartOpen(false);
      setTimeout(() => {
        setExpandedChartKey(undefined);
      }, 500);
      setTimeout(() => {
        setExpandedChartOpen(true);
        setExpandedChartKey(propertiesKey);
      }, 1000);
    } else {
      setExpandedChartOpen(true);
      setExpandedChartKey(propertiesKey);
    }
  };

  return (
    <>
      {historyChartData && (
        <div className="row">
          <div className="col-12">
            <HistoryChart
              title="All"
              labels={historyChartLabels}
              tooltipLabels={tooltipLabels}
              datasets={
                historyChartData.historyChartSeverityData[VulnStatus.All]
              }
              // colors={[chroma("#055193").brighten(1.5).css(), "#055193"]}
              colors={['#055193', '#0096FF', '#000000']}
              showCounts={showHistoryChartCounts}
              getDrillDownUrl={(sourceType) =>
                getHistoryChartsDrillDownUrl('open', sourceType)
              }
              newDataGenerationIndex={newDataGenerationIndex}
              selectedHostType={sastType}
              lastSyncedDate={LastSyncDate}
              currentVulnCount={sastCurrentVulnCount}
            ></HistoryChart>
          </div>

          <Collapse isOpen={expandedChartOpen} className="col-12">
            {expandedChartKey && (
              <HistoryChart
                key="expandedChart"
                {...chartPropertiesDictionary[expandedChartKey]}
                isActive={true}
              />
            )}
          </Collapse>
          <div className="col-md-4">
            <HistoryChart
              {...chartPropertiesDictionary['new']}
              isActive={expandedChartKey === 'new'}
            />
          </div>
          <div className="col-md-4">
            <HistoryChart
              {...chartPropertiesDictionary['reopened']}
              isActive={expandedChartKey === 'reopened'}
            />
          </div>
          <div className="col-md-4">
            <HistoryChart
              {...chartPropertiesDictionary['fixed']}
              isActive={expandedChartKey === 'fixed'}
            />
          </div>
          {/* <div className="col-md-3">
                    <HistoryChart
                        {...chartPropertiesDictionary['ignored']}
                        isActive={expandedChartKey === 'ignored'}
                    />
                </div> */}
        </div>
      )}
    </>
  );
};

export default HistoryCharts;
